<template>
  <v-app id="default-layout">
    <v-toolbar color="peterBlue" fixed app dark style="z-index: 3;">
      <div
        style="height: 40px; display: flex; align-items: center; margin-right: 16px; flex-shrink: 0;"
      >
        <img
          v-if="loginLogoSrc"
          :src="loginLogoSrc"
          alt="Logo"
          style="height: 40px; width: auto; max-width: 200px; object-fit: contain; display: block;"
        />
      </div>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-menu v-model="menu" offset-y v-if="!authenticatedUser.rootUser">
          <template v-slot:activator="{ on }">
            <v-btn flat v-on="on" v-if="pageSelectedVenue"
              >{{ pageSelectedVenue.name }}
              <v-icon right small>keyboard_arrow_down</v-icon>
            </v-btn>
          </template>
          <v-card style="min-width: 150px">
            <v-list>
              <v-list-tile @click="selectVenue(v)" v-for="v in userVenues" :key="v._id">
                <v-list-tile-content>
                  <v-list-tile-title>{{ v.name }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-card>
        </v-menu>
        <!-- User profile dropdown -->
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn flat v-on="on" class="user-profile-btn">
              <v-avatar id="user-avatar" size="32px" class="mr-2" style="border-radius: 50%;">
                <v-img
                  :src="cmsLogoSrc"
                  alt="User Avatar"
                  cover
                  style="border-radius: 50%;"
                ></v-img>
              </v-avatar>
              <span class="user-name">{{ authenticatedUser.firstName || 'Users' }}</span>
              <v-icon right small>keyboard_arrow_down</v-icon>
            </v-btn>
          </template>
          <v-list style="padding: 4px;">
            <!-- Profile - active if current page -->
            <v-list-tile
              @click="navigateToPage('User Profile')"
              ripple
              :class="{ 'dropdown-item-active': isCurrentPage('User Profile') }"
              class="dropdown-item"
            >
              <v-list-tile-action>
                <v-icon small>person</v-icon>
              </v-list-tile-action>
              <v-list-tile-title>Profile</v-list-tile-title>
            </v-list-tile>

            <!-- CMS -->
            <v-list-tile @click="navigateTo('/')" ripple class="dropdown-item primary--text">
              <v-list-tile-action>
                <v-icon small color="peterBlue">dashboard</v-icon>
              </v-list-tile-action>
              <v-list-tile-title>CMS</v-list-tile-title>
            </v-list-tile>

            <!-- Raffle -->
            <v-list-tile
              v-if="!authenticatedUser.rootUser && pageSelectedVenue"
              @click="navigateTo(`/${pageSelectedVenue._id}/raffle`)"
              ripple
              class="dropdown-item"
            >
              <v-list-tile-action>
                <v-icon small>confirmation_number</v-icon>
              </v-list-tile-action>
              <v-list-tile-title>Raffle</v-list-tile-title>
            </v-list-tile>

            <!-- Member Draw -->
            <v-list-tile
              v-if="!authenticatedUser.rootUser && pageSelectedVenue"
              @click="navigateTo(`/${pageSelectedVenue._id}/member-draw`)"
              ripple
              class="dropdown-item"
            >
              <v-list-tile-action>
                <v-icon small>group</v-icon>
              </v-list-tile-action>
              <v-list-tile-title>Member Draw</v-list-tile-title>
            </v-list-tile>

            <v-divider style="margin: 4px 0;"></v-divider>

            <!-- Logout -->
            <v-list-tile @click="logout" ripple class="dropdown-item dropdown-item-logout">
              <v-list-tile-action>
                <v-icon small color="red">exit_to_app</v-icon>
              </v-list-tile-action>
              <v-list-tile-title>Logout</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-toolbar-items>
    </v-toolbar>

    <v-navigation-drawer
      app
      fixed
      permanent
      :value="!isNavigating"
      :mini-variant="false"
      disable-resize-watcher
      disable-route-watcher
      style="z-index: 2"
      width="100px"
      id="app-navigation"
      clipped
      stateless
      :style="{ backgroundColor: currentSidebarColor }"
    >
      <div class="nav-container">
        <div class="nav-content hide-scrollbar">
          <v-list :style="{ padding: '20px 0px !important' }">
            <v-list-tile
              v-for="(item, i) in items"
              :key="i"
              :disabled="hasLicenseError && item !== 'Settings'"
              @click="navigateToPage(item)"
              class="nav-item"
              :class="{ 'nav-item-active': isCurrentPage(item) }"
            >
              <div class="nav-item-content">
                <img :src="getIconForItem(item)" class="nav-icon-svg" :alt="item" />
                <div class="nav-text">{{ item }}</div>
              </div>
            </v-list-tile>
          </v-list>
        </div>

        <!-- Version section - always at bottom -->
        <div class="nav-version">
          <v-divider dark></v-divider>
          <v-list two-line>
            <v-list-tile>
              <v-list-tile-content>
                <v-list-tile-title class="version-title">Version</v-list-tile-title>
                <v-list-tile-sub-title class="version-subtitle">
                  {{ version }} &copy; {{ new Date().getFullYear() }}
                  <span v-if="isFullVersion">(F)</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </div>
      </div>
    </v-navigation-drawer>

    <v-content>
      <v-container fluid full-height :style="isAppBuilder() ? { padding: '0px' } : {}">
        <v-alert v-model="hasLicenseError" type="error">{{ licenseErrorMessage }}</v-alert>
        <router-view v-transition :key="$route.fullPath"></router-view>
        <conversion-progress-bar />
      </v-container>
    </v-content>

    <!-- License Expiration Snackbar-->
    <v-snackbar
      v-model="licenseSnackbar"
      :color="licenseSnackbarColor"
      top
      right
      :timeout="10000"
      multi-line
      class="license-snackbar"
    >
      <v-icon color="white" class="mr-2">warning</v-icon>
      {{ licenseSnackbarMessage }}
      <v-btn flat color="white" @click="licenseSnackbar = false">
        Close
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import moment from 'moment';
import { mapState, mapGetters } from 'vuex';
import { VENUE_STORAGE } from '@/constants';
import mixins from '@/mixins';
import config from '@/config';

import devicesIcon from '@/assets/icons/navbar_icons/devices.svg';
import groupsIcon from '@/assets/icons/navbar_icons/groups.svg';
import assetsIcon from '@/assets/icons/navbar_icons/assets.svg';
import playlistsIcon from '@/assets/icons/navbar_icons/playlists.svg';
import schedulesIcon from '@/assets/icons/navbar_icons/schedules.svg';
import usersIcon from '@/assets/icons/navbar_icons/users.svg';
import venuesIcon from '@/assets/icons/navbar_icons/venus.svg';
import settingsIcon from '@/assets/icons/navbar_icons/settings.svg';
import userProfileIcon from '@/assets/icons/navbar_icons/user_profile.svg';
import appBuilderIcon from '@/assets/icons/navbar_icons/app_builder.svg';
import menuIcon from '@/assets/icons/navbar_icons/menu.svg';
import gameIcon from '@/assets/icons/navbar_icons/games.svg';
import bannerIcon from '@/assets/icons/navbar_icons/view_carousel.svg';
import promotionIcon from '@/assets/icons/navbar_icons/local_offer.svg';
import PlayerPortalIcon from '@/assets/icons/navbar_icons/microwave.svg';
import ConversionProgressBar from '@/components/ConversionProgressBar.vue';
import { VENUE_ASSETS_DEFAULT } from '../constants';
import { getVenueTheme } from '../services/helpers';

export default {
  name: 'default',
  components: {
    ConversionProgressBar
  },
  mixins: [mixins],
  data() {
    return {
      menu: false,
      pageSelectedVenue: null,
      isNavigating: false,
      licenseSnackbar: false,
      licenseSnackbarMessage: '',
      licenseSnackbarColor: 'warning',
      loginLogoSrc: '',
      cmsLogoSrc: ''
    };
  },
  created() {
    this.initializeVenueSelection();
    this.checkAndShowLicenseNotification();
  },
  mounted() {
    this.loadLogos();
    this.applyVenueTheme();
  },
  watch: {
    pageSelectedVenue: {
      handler() {
        this.loadLogos();
      },
      deep: true
    },
    'authenticatedUser.avatar': {
      handler() {
        const avatarUrl = this.userAvatarUrl;
        this.loadImage(avatarUrl, '/static/avatar-placeholder.png').then(src => {
          this.cmsLogoSrc = src;
        });
      },
      immediate: true
    },
    selectedVenue: {
      handler(newVenue) {
        if (newVenue && !this.pageSelectedVenue) {
          this.pageSelectedVenue = newVenue;
        }
      },
      immediate: true
    },
    userVenues: {
      handler(newVenues) {
        if (
          !this.pageSelectedVenue &&
          newVenues &&
          newVenues.length > 0 &&
          !this.authenticatedUser.rootUser
        ) {
          this.selectVenue(newVenues[0]);
        }
      },
      immediate: true
    },
    $route() {
      this.$nextTick(() => {
        this.isNavigating = false;
        this.checkAndShowLicenseNotification();
      });
      this.applyVenueTheme();
    },
    license: {
      handler() {
        this.checkAndShowLicenseNotification();
      },
      deep: true
    }
  },
  methods: {
    initializeVenueSelection() {
      if (!this.pageSelectedVenue) {
        this.pageSelectedVenue = this.selectedVenue;
      }

      if (
        !this.pageSelectedVenue &&
        this.userVenues &&
        this.userVenues.length > 0 &&
        !this.authenticatedUser.rootUser
      ) {
        this.selectVenue(this.userVenues[0]);
      }
    },

    checkAndShowLicenseNotification() {
      if (this.licenseWarning && this.licenseWarning.message) {
        const { localLicense, onlineLicense } = this.licenseWarning;

        let messages = [];

        if (localLicense && localLicense.warning && localLicense.daysLeft > 0) {
          const localExpires = moment(this.license.expires);
          const formattedDate = localExpires.format('HH:mm [on] DD/MM/YYYY');
          messages.push(
            `Your local license will expire in ${localLicense.daysLeft} day(s) at ${formattedDate}.`
          );
        }

        if (onlineLicense && onlineLicense.warning && onlineLicense.daysLeft > 0) {
          messages.push(`Your online license will expire in ${onlineLicense.daysLeft} day(s).`);
        }

        if (messages.length > 0) {
          const fullMessage = messages.join(' ') + ' Please renew to avoid service interruption.';
          this.showLicenseExpirationSnackbar(fullMessage);
        }
      }
    },

    showLicenseExpirationSnackbar(message) {
      this.licenseSnackbarMessage = message;
      this.licenseSnackbarColor = 'warning';
      this.licenseSnackbar = true;
    },

    logout() {
      this.$store
        .dispatch('users/LOGOUT')
        .then(() => {
          this.$router.push({ name: 'Login' });
        })
        .catch(e => {});
    },

    isAppBuilder() {
      return this.$route.name === 'App Builder';
    },

    selectVenue(venue) {
      if (this.$route.params.venueId !== venue._id) {
        localStorage.setItem(VENUE_STORAGE, JSON.stringify(venue));
        this.$vuetify.theme.peterBlue = venue.primaryColor || config.theme.peterBlue;
        this.$vuetify.theme.sidebarColor = venue.secondaryColor || config.theme.sidebarColor;

        this.pageSelectedVenue = venue;

        this.$nextTick(() => {
          this.loadLogos();
        });

        this.$router.push({ name: this.$route.name, params: { venueId: venue._id } });
      }
    },

    getIconForItem(item) {
      const iconMap = {
        Devices: devicesIcon,
        Groups: groupsIcon,
        Assets: assetsIcon,
        Playlists: playlistsIcon,
        Schedules: schedulesIcon,
        Users: usersIcon,
        Venues: venuesIcon,
        Settings: settingsIcon,
        'User Profile': userProfileIcon,
        'Game Admin': gameIcon,
        'Banner Config': bannerIcon,
        'App Builder': appBuilderIcon,
        'Promotion Management': promotionIcon,
        'Player Portal': PlayerPortalIcon
      };

      return iconMap[item] || menuIcon;
    },

    navigateTo(path) {
      window.location.href = path;
    },

    navigateToPage(item) {
      this.isNavigating = true;
      const route = {
        name: item,
        params: { venueId: this.pageSelectedVenue ? this.pageSelectedVenue._id : '' }
      };
      this.$router.push(route);
    },

    isCurrentPage(item) {
      return this.$route.name === item;
    },

    loadLogos() {
      this.loadImage(this.venueLoginLogoUrl, VENUE_ASSETS_DEFAULT.login_logo).then(src => {
        this.loginLogoSrc = src;
      });

      this.loadImage(this.userAvatarUrl, '/static/avatar-placeholder.png').then(src => {
        this.cmsLogoSrc = src;
      });
    },

    loadImage(url, fallback) {
      return new Promise(resolve => {
        if (!url) {
          resolve(fallback);
          return;
        }

        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => resolve(fallback);

        setTimeout(() => resolve(fallback), 3000);

        img.src = url;
      });
    },

    applyVenueTheme() {
      const theme = getVenueTheme();
      if (!theme) return;

      this.$vuetify.theme.peterBlue = theme.peterBlue;
      this.$vuetify.theme.sidebarColor = theme.sidebarColor;
    }
  },
  computed: {
    ...mapGetters({
      version: 'GET_CMS_VERSION',
      isFullVersion: 'GET_IS_FULL_VERSION',
      items: 'GET_MENU_LIST'
    }),
    ...mapState('license', {
      hasLicenseError: 'hasError',
      hasLicenseWarning: 'hasWarning',
      licenseErrorMessage: 'error',
      licenseWarningMessage: 'warning',
      license: 'license',
      licenseWarning: 'licenseWarning'
    }),
    ...mapState('users', {
      authenticatedUser: 'user'
    }),
    currentSidebarColor() {
      return this.$vuetify.theme.sidebarColor || config.theme.sidebarColor;
    },
    venueLoginLogoUrl() {
      if (!this.pageSelectedVenue) return null;
      const venueAsset = this.pageSelectedVenue.venueAsset;
      if (!venueAsset) return null;

      // API upload saves to cms_logo, use it for toolbar logo
      const logo = venueAsset.cms_logo || venueAsset.login_logo;
      if (!logo) return null;

      if (logo.startsWith('http://') || logo.startsWith('https://')) {
        return logo.replace(/([^:]\/)\/+/g, '$1');
      }

      const path = logo.startsWith('/') ? logo : `/${logo}`;
      return `${config.server_url}${path}`;
    },
    userAvatarUrl() {
      if (!this.authenticatedUser || !this.authenticatedUser.avatar) return null;
      const avatar = this.authenticatedUser.avatar;
      if (avatar.startsWith('http://') || avatar.startsWith('https://')) return avatar;

      // ensure leading slash for relative paths
      const path = avatar.startsWith('/') ? avatar : `/${avatar}`;
      return `${config.server_url}${path}`;
    }
  },
  filters: {
    fullDate(val) {
      return moment(val).format('MMMM D, YYYY h:mm A');
    }
  }
};
</script>

<style>
#default-layout .v-content {
  background: #ffffff !important;
}

#default-layout nav.v-toolbar {
  padding-left: 0px !important;
}

#default-layout .v-toolbar__content {
  padding-left: 12px !important;
  padding-right: 8px !important;
}

#app-navigation .v-navigation-drawer__content {
  overflow-x: hidden !important;
}

#app-navigation .v-list {
  padding: 20px 0px !important;
}

.nav-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.nav-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-version .v-list__tile {
  height: auto !important;
  min-height: 40px;
  padding: 8px 12px !important;
}

.nav-version .v-list__tile__content {
  height: auto !important;
}

.nav-version .v-list__tile__title,
.version-title {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.9) !important;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  text-align: center;
}

.nav-version .v-list__tile__sub-title,
.version-subtitle {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7) !important;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-wrap: break-word;
  line-height: 1.3;
}

.nav-version .v-list--two-line .v-list__tile {
  height: 40px;
}

.nav-item {
  height: auto !important;
  margin-bottom: 8px;
  padding: 0 !important;
  cursor: pointer;
}

.nav-item .v-list__tile {
  padding: 0 !important;
  height: auto !important;
  justify-content: center;
}

.nav-item-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 84px;
  min-height: 70px;
  height: auto;
  gap: 8px;
  padding: 8px;
  border-radius: 9px;
  border: 1px solid transparent;
  transition: all 300ms ease;
  position: relative;
  background: transparent;
}

.nav-text {
  font-size: 11px;
  text-align: center;
  color: #808290;
  transition: all 300ms ease;
  line-height: 1.2;
  position: relative;
  z-index: 1;
  white-space: normal;
  max-width: 100%;
  overflow: visible;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
}

.nav-item-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 9px;
  opacity: 0;
  transition: opacity 300ms ease;
  pointer-events: none;
}

.nav-icon-svg {
  width: 24px;
  height: 24px;
  filter: brightness(0) saturate(0%) invert(52%) sepia(8%) saturate(446%) hue-rotate(201deg)
    brightness(93%) contrast(88%);
  transition: filter 300ms ease;
  position: relative;
  z-index: 1;
}

.nav-item:not(.nav-item-active):hover .nav-item-content::before {
  opacity: 1;
}

.nav-item:not(.nav-item-active):hover .nav-item-content {
  border: 1px solid #4fb9ff;
}

.nav-item:not(.nav-item-active):hover .nav-icon-svg {
  filter: brightness(0) saturate(100%) invert(60%) sepia(90%) saturate(3000%) hue-rotate(180deg)
    brightness(110%);
}

.nav-item:not(.nav-item-active):hover .nav-text {
  background: linear-gradient(270deg, #4fb9ff 0%, #03a7f9 50%, #3681f9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-item-active .nav-item-content::before {
  opacity: 1;
}

.nav-item-active .nav-item-content {
  border: 1px solid #4fb9ff;
}

.nav-item-active .nav-icon-svg {
  filter: brightness(0) saturate(100%) invert(60%) sepia(90%) saturate(3000%) hue-rotate(180deg)
    brightness(110%);
}

.nav-item-active .nav-text {
  background: linear-gradient(270deg, #4fb9ff 0%, #03a7f9 50%, #3681f9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 500;
}

#default-layout .v-list__tile {
  height: auto;
  padding: 4px 16px;
}

#default-layout .v-list__tile--active {
  border-radius: 8px;
}

.user-profile-section {
  height: 100%;
}

.apac-lab-text {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.5px;
}

.user-profile-btn {
  height: 48px !important;
  margin: 0 !important;
  padding: 0 8px !important;
  min-width: auto !important;
  text-transform: none !important;
}

.user-profile-btn:before {
  background-color: transparent !important;
}

.user-profile-btn:hover:before {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: white;
  margin-right: 4px;
}

.user-profile-btn .v-avatar {
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.user-profile-btn .v-icon {
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px !important;
}

#user-avatar {
  border: 2px solid rgba(255, 255, 255, 0.5) !important;
  border-radius: 50% !important;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.15);
}

#user-avatar .v-image__image {
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  background-size: cover !important;
}

.license-snackbar {
  position: fixed;
  top: 40px;
  left: 0;
  right: 0;
  z-index: 1000;
  margin: auto;
}

.license-snackbar .v-snack__content {
  font-size: 14px;
  font-weight: 500;
}

.dropdown-item .v-list__tile {
  border-radius: 8px;
  margin: 2px 0;
  transition: background 0.15s ease;
}

.dropdown-item .v-list__tile__action {
  min-width: 32px !important;
}

.dropdown-item:hover .v-list__tile {
  background: #f4f6fc;
}

.dropdown-item-active .v-list__tile {
  background: #eff6ff !important;
}

.dropdown-item-active .v-list__tile__title {
  color: #114470 !important;
  font-weight: 600;
}

.dropdown-item-active .v-icon {
  color: #114470 !important;
}

.dropdown-item-logout .v-list__tile__title {
  color: #ef4444;
}

.dropdown-item-logout:hover .v-list__tile {
  background: #fef2f2;
}
</style>



// WEBPACK FOOTER //
// src/layouts/default.vue